import { cva } from "class-variance-authority";
import React, { useCallback } from "react";

import { faAngleDown } from "@performyard/fontawesome/pro-regular-svg-icons";

import { cn } from "../../../lib/utils";

import { Icon, type IconProps } from "../../atoms";

const accordionHeaderVariants = cva(
	"group flex min-h-12 w-full items-center justify-between px-4 py-3 text-left transition-colors select-none",
	{
		variants: {
			variant: {
				standard: "",
				underline: "",
			},
			isOpen: {
				true: "",
				false: "",
			},
			disabled: {
				true: "cursor-not-allowed opacity-50",
				false: "hover:cursor-pointer",
			},
			size: {
				small: "min-h-10",
				large: "min-h-12",
			},
		},
		compoundVariants: [
			// 1. standard collapsed
			{
				variant: "standard",
				isOpen: false,
				className: "rounded-lg border border-solid border-neutral-3 bg-neutral-1",
			},
			// 2. Standard hover
			{
				variant: "standard",
				isOpen: false,
				disabled: false,
				className: "hover:border-neutral-3 hover:bg-neutral-2",
			},
			// 3. Standard expanded
			{
				variant: "standard",
				isOpen: true,
				className:
					"rounded-t-lg border border-b-2 border-solid border-neutral-3 border-b-transparent hover:border-b-neutral-3",
			},
			// 4. Underline collapsed
			{
				variant: "underline",
				isOpen: false,
				className: "border-x-0 border-t-0 border-b border-solid border-neutral-2 bg-transparent",
			},
			// 5. Underline hover
			{
				variant: "underline",
				isOpen: false,
				disabled: false,
				className: "hover:border-b-neutral-3",
			},
			// 6. Underline expanded
			{
				variant: "underline",
				isOpen: true,
				className:
					"border-x-0 border-t-0 border-b border-solid border-neutral-3 bg-transparent hover:border-b-neutral-4",
			},
		],
		defaultVariants: {
			variant: "standard",
			isOpen: false,
			disabled: false,
		},
	}
);

const accordionContentVariants = cva("bg-neutral-0 px-4 py-3", {
	variants: {
		variant: {
			standard: "-mt-px rounded-b-lg border-x border-t-0 border-b border-solid border-neutral-3",
			underline: "",
		},
	},
	defaultVariants: {
		variant: "standard",
	},
});

export type AccordionVariant = "standard" | "underline";

export interface AccordionProps {
	/**
	 * Whether the accordion is open/expanded
	 */
	isOpen: boolean;
	/**
	 * Function called when the accordion header is clicked
	 */
	onToggle: () => void;
	/**
	 * Visual variant of the accordion
	 * - "standard": Full border with rounded corners, follows MeetingAccordion styling
	 * - "underline": Bottom border only when collapsed, transparent background, no rounded corners
	 */
	variant?: AccordionVariant;
	/**
	 * Size of the accordion
	 * - "small": Smaller accordion with smaller header and content
	 * - "large": Larger accordion with larger header and content
	 */
	size?: "small" | "large";
	/**
	 * Icon props for the header icon (optional)
	 * Allows full control over icon appearance including color, size, etc.
	 */
	headerIcon?: Omit<IconProps, "dangerouslySetClassName">;
	/**
	 * Content for the header - can be any React node
	 * Takes up the full width between the left icon and the right chevron
	 * Implementers can pass any custom layout, text, or components
	 */
	headerContent: React.ReactNode;
	/**
	 * Content to display when accordion is expanded
	 */
	children: React.ReactNode;
	/**
	 * Optional class name for the accordion container
	 */
	className?: string;
	/**
	 * Test ID for the accordion
	 */
	"data-testid"?: string;
	/**
	 * Disable the accordion interaction
	 */
	disabled?: boolean;
}

/**
 * A reusable accordion component that follows the styling patterns of MeetingAccordion
 * but is more extensible for general use cases.
 *
 * Features:
 * - Two variants: "standard" (full border) and "underline" (bottom border only)
 * - Configurable header with optional icon and flexible content area
 * - Header content takes full width between icon and chevron
 * - Dynamic header height with consistent 12px padding (min-height: 48px)
 * - Width fills container
 * - Smooth animations with rotating chevron
 * - Proper disabled states with no hover effects
 * - Accessible with ARIA attributes and keyboard support
 */
const Accordion = (props: AccordionProps) => {
	const {
		isOpen = false,
		onToggle,
		variant = "standard",
		size = "large",
		headerIcon,
		headerContent,
		children,
		className,
		disabled = false,
		"data-testid": dataTestId,
	} = props;

	const handleToggle = useCallback(() => {
		if (!disabled) {
			onToggle();
		}
	}, [disabled, onToggle]);

	return (
		<div className={cn("flex w-full flex-col", className)} data-testid={dataTestId}>
			{/* Accordion Header */}
			<button
				className={cn(accordionHeaderVariants({ variant, isOpen, disabled, size }))}
				onClick={handleToggle}
				disabled={disabled}
				data-testid={dataTestId ? `${dataTestId}-header` : "accordion-header"}
				aria-expanded={isOpen}
				aria-label={`${isOpen ? "Collapse" : "Expand"} accordion`}
			>
				<div className="flex min-w-0 flex-1 items-center gap-2">
					{/* Header Icon */}
					{headerIcon && (
						<div className="flex shrink-0 items-center justify-center">
							<Icon {...headerIcon} />
						</div>
					)}

					{/* Header Content - takes full width */}
					<div className="flex min-w-0 flex-1 items-center">{headerContent}</div>
				</div>

				{/* Chevron Icon */}
				<div className="ml-2 flex shrink-0 items-center">
					<div className={isOpen ? "rotate-180 transition duration-300" : "rotate-0 transition duration-300"}>
						<Icon
							icon={faAngleDown}
							color="neutral-5"
							dangerouslySetClassName="group-hover:text-neutral-7"
							size={size === "small" ? 16 : 24}
						/>
					</div>
				</div>
			</button>

			{/* Accordion Content */}
			{isOpen && (
				<div
					className={accordionContentVariants({ variant })}
					data-testid={dataTestId ? `${dataTestId}-content` : "accordion-content"}
				>
					{children}
				</div>
			)}
		</div>
	);
};

Accordion.displayName = "Accordion";

export { Accordion, accordionHeaderVariants, accordionContentVariants };
